import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Form, Spinner, Modal } from 'react-bootstrap';
import { Trash2 } from 'lucide-react';
import { SectionLoader } from '../../Components/LoadingSpinner';
import { getDatabase, ref, set, get, update } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { formatDateForStorage } from '../../Helpers/DateUtils';
import { tombstoneStorageOnlyFile } from '../../api/fileDeletionApi';
import { recordActivity } from '../../api/activityApi';
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import { FileInputControl } from '../../Components/FileInputControl';

interface LoginPageConfig {
    backgroundImageUrl?: string;
    customerLogoUrl?: string;
    smallLogoUrl?: string;
    lastUpdatedBy?: string;
    lastUpdatedDate?: string;
}

const LoginPageSettings: React.FC = () => {
    const navigate = useNavigate();
    const [config, setConfig] = useState<LoginPageConfig>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'danger', text: string } | null>(null);
    
    const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [smallLogoFile, setSmallLogoFile] = useState<File | null>(null);
    const [backgroundPreview, setBackgroundPreview] = useState<string | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [smallLogoPreview, setSmallLogoPreview] = useState<string | null>(null);

    // Drag and drop state
    const [isBackgroundDragOver, setIsBackgroundDragOver] = useState(false);
    const [isLogoDragOver, setIsLogoDragOver] = useState(false);
    const [isSmallLogoDragOver, setIsSmallLogoDragOver] = useState(false);

    // Modal state for removal confirmation
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeType, setRemoveType] = useState<'background' | 'logo' | 'smallLogo' | null>(null);

    const auth = getAuth();
    const database = getDatabase();
    const storage = getStorage();

    // Load current configuration
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const configRef = ref(database, 'publicSettings/loginPage');
                const snapshot = await get(configRef);
                
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    setConfig(data);
                    setBackgroundPreview(data.backgroundImageUrl || null);
                    setLogoPreview(data.customerLogoUrl || null);
                    setSmallLogoPreview(data.smallLogoUrl || null);
                }
            } catch (error) {
                console.error('Error loading login page config:', error);
                setMessage({ type: 'danger', text: 'Kunde inte ladda konfiguration' });
            } finally {
                setLoading(false);
            }
        };

        loadConfig();
    }, [database]);

    const handleBackgroundFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'danger', text: 'Vänligen välj en bildfil' });
                return;
            }
            setBackgroundFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setBackgroundPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'danger', text: 'Vänligen välj en bildfil' });
                return;
            }
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSmallLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'danger', text: 'Vänligen välj en bildfil' });
                return;
            }
            setSmallLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setSmallLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    // Drag and drop handlers - generic
    const createDragHandlers = (
        setDragOver: (value: boolean) => void,
        setFile: (file: File) => void,
        setPreview: (url: string) => void
    ) => ({
        onDragOver: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(true);
        },
        onDragLeave: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setDragOver(false);
            }
        },
        onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);

            const files = Array.from(e.dataTransfer.files);
            const imageFile = files.find(f => f.type.startsWith('image/'));
            if (!imageFile) {
                setMessage({ type: 'danger', text: 'Vänligen välj en bildfil' });
                return;
            }

            setFile(imageFile);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result as string);
            };
            reader.readAsDataURL(imageFile);
        }
    });

    const backgroundDragHandlers = createDragHandlers(setIsBackgroundDragOver, setBackgroundFile, setBackgroundPreview);
    const logoDragHandlers = createDragHandlers(setIsLogoDragOver, setLogoFile, setLogoPreview);
    const smallLogoDragHandlers = createDragHandlers(setIsSmallLogoDragOver, setSmallLogoFile, setSmallLogoPreview);

    const uploadImage = async (file: File, path: string): Promise<string> => {
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, file);
        const downloadUrl = await getDownloadURL(fileRef);
        return downloadUrl;
    };

    const openImageInFancybox = (imageUrl: string, caption: string) => {
        Fancybox.show([{
            src: imageUrl,
            caption: caption
        }], {
            showClass: "fancybox-fadeIn"
        });
    };

    const handleRemoveBackground = () => {
        setRemoveType('background');
        setShowRemoveModal(true);
    };

    const handleRemoveLogo = () => {
        setRemoveType('logo');
        setShowRemoveModal(true);
    };

    const handleRemoveSmallLogo = () => {
        setRemoveType('smallLogo');
        setShowRemoveModal(true);
    };

    const confirmRemove = async () => {
        setShowRemoveModal(false);
        setSaving(true);
        setMessage(null);

        try {
            if (removeType === 'background') {
                // Tombstone the old file so a future cleanup job can purge the blob
                if (config.backgroundImageUrl) {
                    try {
                        await tombstoneStorageOnlyFile('brandingAsset', 'publicAssets/loginPage/background');
                    } catch (error) {
                        // Best effort - tombstone failure should not block removal
                    }
                }

                // Update database - use update with null to remove the field
                const configRef = ref(database, 'publicSettings/loginPage');
                await update(configRef, {
                    backgroundImageUrl: null,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                });

                // Update local state
                const updatedConfig = {
                    ...config,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                };
                delete updatedConfig.backgroundImageUrl;

                setConfig(updatedConfig);
                setBackgroundPreview(null);
                setBackgroundFile(null);
                setMessage({ type: 'success', text: 'Bakgrundsbild borttagen' });
            } else if (removeType === 'logo') {
                // Tombstone the old file so a future cleanup job can purge the blob
                if (config.customerLogoUrl) {
                    try {
                        await tombstoneStorageOnlyFile('brandingAsset', 'publicAssets/loginPage/logo');
                    } catch (error) {
                        // Best effort - tombstone failure should not block removal
                    }
                }

                // Update database - use update with null to remove the field
                const configRef = ref(database, 'publicSettings/loginPage');
                await update(configRef, {
                    customerLogoUrl: null,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                });

                // Update local state
                const updatedConfig = {
                    ...config,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                };
                delete updatedConfig.customerLogoUrl;

                setConfig(updatedConfig);
                setLogoPreview(null);
                setLogoFile(null);
                setMessage({ type: 'success', text: 'Logotyp borttagen' });
            } else if (removeType === 'smallLogo') {
                // Tombstone the old file so a future cleanup job can purge the blob
                if (config.smallLogoUrl) {
                    try {
                        await tombstoneStorageOnlyFile('brandingAsset', 'publicAssets/loginPage/smallLogo');
                    } catch (error) {
                        // Best effort - tombstone failure should not block removal
                    }
                }

                // Update database - use update with null to remove the field
                const configRef = ref(database, 'publicSettings/loginPage');
                await update(configRef, {
                    smallLogoUrl: null,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                });

                // Update local state
                const updatedConfig = {
                    ...config,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                };
                delete updatedConfig.smallLogoUrl;

                setConfig(updatedConfig);
                setSmallLogoPreview(null);
                setSmallLogoFile(null);
                setMessage({ type: 'success', text: 'Liten logotyp borttagen' });
            }
            if (removeType) recordActivity('settings.updated');
        } catch (error) {
            console.error('Error removing image:', error);
            setMessage({ type: 'danger', text: 'Kunde inte ta bort bilden' });
        } finally {
            setSaving(false);
            setRemoveType(null);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);

        try {
            const updatedConfig: LoginPageConfig = { ...config };

            // Upload background image if selected
            if (backgroundFile) {
                const backgroundUrl = await uploadImage(backgroundFile, 'publicAssets/loginPage/background');
                updatedConfig.backgroundImageUrl = backgroundUrl;
            }

            // Upload logo if selected
            if (logoFile) {
                const logoUrl = await uploadImage(logoFile, 'publicAssets/loginPage/logo');
                updatedConfig.customerLogoUrl = logoUrl;
            }

            // Upload small logo if selected
            if (smallLogoFile) {
                const smallLogoUrl = await uploadImage(smallLogoFile, 'publicAssets/loginPage/smallLogo');
                updatedConfig.smallLogoUrl = smallLogoUrl;
            }

            // Add metadata
            updatedConfig.lastUpdatedBy = auth.currentUser?.uid || '';
            updatedConfig.lastUpdatedDate = formatDateForStorage(new Date());

            // Save to database
            const configRef = ref(database, 'publicSettings/loginPage');
            await set(configRef, updatedConfig);
            recordActivity('settings.updated');

            setConfig(updatedConfig);
            setBackgroundFile(null);
            setLogoFile(null);
            setSmallLogoFile(null);
            setMessage({ type: 'success', text: 'Inställningar sparade!' });
        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage({ type: 'danger', text: 'Kunde inte spara inställningar. Kontrollera att du har behörighet.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <SectionLoader />;
    }

    return (
        <div className="login-page-settings" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div className="mb-4">
                <h2 style={{ fontSize: '24px', fontWeight: 500, marginBottom: '8px' }}>Identitet</h2>
                <p className="text-muted" style={{ fontSize: '16px', marginBottom: 0 }}>
                    Anpassa bilder som visas på inloggningssidan och i appen
                </p>
            </div>

            {message && (
                <Alert 
                    variant={message.type} 
                    dismissible 
                    onClose={() => setMessage(null)}
                    className="mb-4"
                >
                    {message.text}
                </Alert>
            )}

            <div className="mb-4" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                {/* Background Image Card */}
                <Card
                    className="std-box dropshadow login-settings-card"
                    style={{
                        border: isBackgroundDragOver ? '2px dashed #007bff' : 'none',                        flex: '1 1 280px',
                        backgroundColor: isBackgroundDragOver ? '#f0f8ff' : 'white',
                        transition: 'all 0.2s ease'
                    }}
                    {...backgroundDragHandlers}
                >
                    <Card.Body style={{ padding: '16px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Bakgrundsbild inloggning</h5>
                            {backgroundPreview && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={handleRemoveBackground}
                                    disabled={saving}
                                    title="Ta bort bakgrundsbild"
                                    aria-label="Ta bort bakgrundsbild"
                                    style={{
                                        color: '#dc3545',
                                        padding: '0',
                                        lineHeight: 0
                                    }}
                                >
                                    <Trash2 size={16} />
                                </Button>
                            )}
                        </div>

                        {backgroundPreview ? (
                            <div className="mb-2">
                                <img 
                                    src={backgroundPreview}
                                    alt="Förhandsvisning av bakgrundsbild"
                                    onClick={() => openImageInFancybox(backgroundPreview, 'Bakgrundsbild')}
                                    style={{ 
                                        width: '100%', 
                                        height: '140px', 
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        border: '1px solid #e0e0e0',
                                        cursor: 'pointer'
                                    }} 
                                />
                            </div>
                        ) : (
                            <div 
                                className="mb-2 d-flex align-items-center justify-content-center"
                                style={{ 
                                    width: '100%', 
                                    height: '140px', 
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    border: '2px dashed #dee2e6',
                                    color: '#6c757d',
                                    fontSize: '12px'
                                }}
                            >
                                Ingen bild vald
                            </div>
                        )}

                        <FileInputControl
                            id="background-file-input"
                            fileName={backgroundFile?.name}
                            onChange={handleBackgroundFileChange}
                            disabled={saving}
                        />
                    </Card.Body>
                </Card>

                {/* Logo Card */}
                <Card
                    className="std-box dropshadow login-settings-card"
                    style={{
                        border: isLogoDragOver ? '2px dashed #007bff' : 'none',                        flex: '1 1 280px',
                        backgroundColor: isLogoDragOver ? '#f0f8ff' : 'white',
                        transition: 'all 0.2s ease'
                    }}
                    {...logoDragHandlers}
                >
                    <Card.Body style={{ padding: '16px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Logotyp inloggning</h5>
                                {logoPreview && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={handleRemoveLogo}
                                        disabled={saving}
                                        title="Ta bort logotyp"
                                        aria-label="Ta bort logotyp"
                                        style={{
                                            color: '#dc3545',
                                            padding: '0',
                                            lineHeight: 0
                                        }}
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                )}
                            </div>

                            {logoPreview ? (
                                <div className="mb-2">
                                    <div 
                                        className="d-flex align-items-center justify-content-center"
                                        onClick={() => openImageInFancybox(logoPreview, 'Logotyp')}
                                        style={{ 
                                            width: '100%', 
                                            height: '140px', 
                                            backgroundColor: '#ffffff',
                                            borderRadius: '6px',
                                            border: '1px solid #e0e0e0',
                                            padding: '12px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <img 
                                            src={logoPreview}
                                            alt="Förhandsvisning av logotyp"
                                            style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: '100%', 
                                                objectFit: 'contain',
                                                pointerEvents: 'none'
                                            }} 
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div 
                                    className="mb-2 d-flex align-items-center justify-content-center"
                                    style={{ 
                                        width: '100%', 
                                        height: '140px', 
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '6px',
                                        border: '2px dashed #dee2e6',
                                        color: '#6c757d',
                                        fontSize: '12px'
                                    }}
                                >
                                    Ingen logotyp vald
                                </div>
                            )}

                            <FileInputControl
                                id="logo-file-input"
                                fileName={logoFile?.name}
                                onChange={handleLogoFileChange}
                                disabled={saving}
                            />
                    </Card.Body>
                </Card>

                {/* Small Logo Card */}
                <Card
                    className="std-box dropshadow login-settings-card"
                    style={{
                        border: isSmallLogoDragOver ? '2px dashed #007bff' : 'none',                        flex: '1 1 280px',
                        backgroundColor: isSmallLogoDragOver ? '#f0f8ff' : 'white',
                        transition: 'all 0.2s ease'
                    }}
                    {...smallLogoDragHandlers}
                >
                    <Card.Body style={{ padding: '16px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Liten logotyp</h5>
                            {smallLogoPreview && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={handleRemoveSmallLogo}
                                    disabled={saving}
                                    title="Ta bort liten logotyp"
                                    aria-label="Ta bort liten logotyp"
                                    style={{
                                        color: '#dc3545',
                                        padding: '0',
                                        lineHeight: 0
                                    }}
                                >
                                    <Trash2 size={16} />
                                </Button>
                            )}
                        </div>

                        {smallLogoPreview ? (
                            <div className="mb-2">
                                <div 
                                    className="d-flex align-items-center justify-content-center"
                                    onClick={() => openImageInFancybox(smallLogoPreview, 'Liten logotyp')}
                                    style={{ 
                                        width: '100%', 
                                        height: '140px', 
                                        backgroundColor: '#ffffff',
                                        borderRadius: '6px',
                                        border: '1px solid #e0e0e0',
                                        padding: '12px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <img 
                                        src={smallLogoPreview}
                                        alt="Förhandsvisning av liten logotyp"
                                        style={{ 
                                            maxWidth: '100%', 
                                            maxHeight: '100%', 
                                            objectFit: 'contain',
                                            pointerEvents: 'none'
                                        }} 
                                    />
                                </div>
                            </div>
                        ) : (
                            <div 
                                className="mb-2 d-flex align-items-center justify-content-center"
                                style={{ 
                                    width: '100%', 
                                    height: '140px', 
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    border: '2px dashed #dee2e6',
                                    color: '#6c757d',
                                    fontSize: '12px'
                                }}
                            >
                                Ingen liten logotyp vald
                            </div>
                        )}

                        <FileInputControl
                            id="small-logo-file-input"
                            fileName={smallLogoFile?.name}
                            onChange={handleSmallLogoFileChange}
                            disabled={saving}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: '13px', display: 'block', marginTop: '6px' }}>
                            Används i navigationspanelen
                        </Form.Text>
                    </Card.Body>
                </Card>
            </div>

            <div>
                <Button 
                    variant="primary" 
                    className="login-settings-save"
                    onClick={handleSave}
                    disabled={saving || (!backgroundFile && !logoFile && !smallLogoFile)}
                    style={{ 
                        borderRadius: '8px',
                        padding: '8px 24px'
                    }}
                >
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sparar...
                        </>
                    ) : (
                        'Spara ändringar'
                    )}
                </Button>
            </div>

            {/* Remove Confirmation Modal */}
            <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta borttagning</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {removeType === 'background' 
                        ? 'Är du säker på att du vill ta bort bakgrundsbilden?'
                        : removeType === 'logo'
                        ? 'Är du säker på att du vill ta bort logotypen?'
                        : 'Är du säker på att du vill ta bort den lilla logotypen?'
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={confirmRemove}>
                        Ta bort
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default LoginPageSettings;

